import React, { type ReactNode } from "react";
import cx from "classnames";

import { ReactComponent as ExoticTag } from "@/features/betslip/assets/exotic.svg";
import { ReactComponent as ComboTag } from "@/features/betslip/assets/combo.svg";
import { ReactComponent as QuaddieTag } from "@/features/betslip/assets/quaddie.svg";
import { ReactComponent as QuinellaTag } from "@/features/betslip/assets/quinella.svg";
import { ReactComponent as ExactaTag } from "@/features/betslip/assets/exacta.svg";
import { ReactComponent as TrifectaTag } from "@/features/betslip/assets/trifecta.svg";
import { ReactComponent as First4Tag } from "@/features/betslip/assets/first4.svg";
import { SemBadge } from "@/features/betslip/components/parts/SemBadge";

import * as styles from "@/features/betslip/components/parts/ComboGroup.module.scss";

type Variant =
  | "exotic"
  | "combo"
  | "quaddie"
  | "srm"
  | "sgm"
  | "quinella"
  | "exacta"
  | "trifecta"
  | "first4";

type ComboGroupProps = {
  variant: Variant;
  label: ReactNode;
  /** Semibold continuation of the label (e.g. "@ 20.25"). */
  boldLabel?: ReactNode;
  /** Right-aligned header control (e.g. the multi leg radio). */
  action?: ReactNode;
  /** Multi-only: this SEM is the leg picked into the multi, shown with the
   *  accent border. Srm/sgm groups render borderless otherwise (exotic and
   *  combo groups keep their permanent tinted border). */
  selected?: boolean;
  children?: ReactNode;
  testId?: string;
};

const tagFor = (variant: Variant) => {
  switch (variant) {
    case "exotic":
      return <ExoticTag />;
    case "combo":
      return <ComboTag />;
    case "quaddie":
      return <QuaddieTag />;
    case "quinella":
      return <QuinellaTag />;
    case "exacta":
      return <ExactaTag />;
    case "trifecta":
      return <TrifectaTag />;
    case "first4":
      return <First4Tag />;
    default:
      return <SemBadge variant={variant} />;
  }
};

export const ComboGroup = ({
  variant,
  label,
  boldLabel,
  action,
  selected = false,
  children,
  testId,
}: ComboGroupProps) => (
  <div
    className={cx(styles.group, styles[variant], {
      [styles.selected]: selected,
    })}
    data-testid={testId}
  >
    <div className={styles.header}>
      <span className={styles.tag}>{tagFor(variant)}</span>
      <span className={styles.label}>
        {label}
        {boldLabel ? (
          <span className={styles.boldLabel}>{boldLabel}</span>
        ) : null}
      </span>
      {action ? <span className={styles.action}>{action}</span> : null}
    </div>
    {children ? <div className={styles.slot}>{children}</div> : null}
  </div>
);
